import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";

// Import documentation images
import img01 from "../../images/project/1.jpg";
import img02 from "../../images/project/2.jpg";
import img03 from "../../images/project/3.jpg";
import img04 from "../../images/project/4.jpg";
import img05 from "../../images/project/img02.jpg";
import img06 from "../../images/project/img03.jpg";

const projectImages: Record<string, string> = {
  "enterprise-rag-sap-integration": img01,
  "omnichannel-whatsapp-ai-agent": img02,
  "intelligent-invoice-logistics": img03,
  "iot-predictive-maintenance": img04,
  "computer-vision-pcb-inspection": img05,
  "dynamic-pricing-retail": img06,
  "1": img01,
  "2": img02,
  "3": img03,
  "4": img04,
  "5": img05,
  "6": img06,
};

// Arrow SVG icon
const ArrowIcon: React.FC = () => (
  <>
    {[...Array(2)].map((_, i) => (
      <svg
        key={i}
        width="25"
        height="25"
        viewBox="0 0 25 25"
        fill="none"
      >
        <rect
          x="5.23"
          y="17.32"
          width="17.88"
          height="2.27"
          transform="rotate(-40.28 5.23 17.32)"
          fill="white"
        />
        <rect
          x="7.75"
          y="6.25"
          width="2.27"
          height="2.27"
          transform="rotate(-40.28 7.75 6.25)"
          fill="white"
        />
        <rect
          x="10.95"
          y="6.52"
          width="2.27"
          height="2.27"
          transform="rotate(-40.28 10.95 6.52)"
          fill="white"
        />
        <rect
          x="14.16"
          y="6.78"
          width="2.27"
          height="2.27"
          transform="rotate(-40.28 14.16 6.78)"
          fill="white"
        />
        <rect
          x="17.09"
          y="10.25"
          width="2.27"
          height="2.27"
          transform="rotate(-40.28 17.09 10.25)"
          fill="white"
        />
        <rect
          x="16.83"
          y="13.45"
          width="2.27"
          height="2.27"
          transform="rotate(-40.28 16.83 13.45)"
          fill="white"
        />
        <rect
          x="16.56"
          y="16.65"
          width="2.27"
          height="2.27"
          transform="rotate(-40.28 16.56 16.65)"
          fill="white"
        />
      </svg>
    ))}
  </>
);

const ProjectSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section className="service pos-rel pt-30 pb-120">
      <div className="container mxw-1650">
        <div className="row g-4 g-lg-5">
          {t.projects.items.map((item, index) => {
            const isReversed = index % 2 === 1;
            const docImage =
              projectImages[item.slug] || projectImages[item.id] || img01;
            const caseUrl = `/project-details/${item.slug}`;

            return (
              <div className="col-12 mb-4" key={item.id}>
                <div className="pca-project-card-inner" style={{ position: "relative" }}>
                  <div
                    className={`row g-4 g-lg-5 align-items-center w-100 m-0 ${
                      isReversed
                        ? "flex-column-reverse flex-lg-row-reverse"
                        : "flex-column-reverse flex-lg-row"
                    }`}
                  >
                    {/* Content Column */}
                    <div className="col-lg-6 pca-project-col-content p-0 pe-lg-4 ps-lg-4">
                      <div className="pca-project-tag-wrap">
                        <span className="pca-project-tag-pill">
                          <span className="pca-pulse-dot"></span>
                          {item.industry}
                        </span>
                        {/* <span className="pca-project-client-pill">
                          {item.client} • {item.location}
                        </span> */}
                      </div>

                      <h2 className="pca-project-title">
                        <Link to={caseUrl}>{item.title}</Link>
                      </h2>

                      <p className="pca-project-desc">{item.description}</p>

                      {/* Impact Box */}
                      <div className="pca-project-impact-box">
                        <div className="pca-impact-title">{t.projects.impactLabel}</div>
                        <ul className="pca-impact-list">
                          {item.impact.map((imp, idx) => (
                            <li key={idx}>
                              <span className="pca-check-icon">✓</span>
                              <span>{imp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Technologies & CTA Button */}
                      <div className="pca-project-bottom-wrap">
                        <div className="pca-tech-stack-wrap">
                          <span className="pca-tech-label">
                            {t.projects.technologiesLabel}
                          </span>
                          <div className="pca-tech-badges">
                            {item.techs.map((tech, i) => (
                              <span key={i} className="pca-tech-badge">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="pca-project-btn-wrap">
                          <Link className="thm-btn agency-btn" to={caseUrl}>
                            <span className="text">{t.projects.readMore}</span>
                            <span className="arrow">
                              <span className="arrow-icon">
                                <ArrowIcon />
                              </span>
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Visual Mockup Column */}
                    <div className="col-lg-6 pca-project-col-visual p-0 pe-lg-3 ps-lg-3">
                      <Link
                        to={caseUrl}
                        className="pca-mockup-frame d-block"
                        title={item.title}
                      >
                        {/* Window Header */}
                        <div className="pca-mockup-header">
                          <div className="pca-mockup-dots">
                            <span className="pca-dot dot-red"></span>
                            <span className="pca-dot dot-yellow"></span>
                            <span className="pca-dot dot-green"></span>
                          </div>
                          <div className="pca-mockup-address">
                            <i
                              className="fas fa-lock me-1"
                              style={{ fontSize: "10px", color: "#00FF97" }}
                            ></i>
                            <span>
                              enterprise.primaciptaartifisial.co.id/{item.slug}
                            </span>
                          </div>
                          <div className="pca-mockup-live-badge">
                            <span className="live-indicator"></span>
                            LIVE
                          </div>
                        </div>

                        {/* Image Body */}
                        <div className="pca-mockup-body">
                          <img
                            src={docImage}
                            alt={item.title}
                            className="pca-mockup-img"
                            loading="lazy"
                          />
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProjectSection;
